import React, {useLayoutEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'

import {Text, TextVariant} from '@/components/common/content/Text'
import {theme} from '@/theme/theme'

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

type TooltipProps = {
  tooltip: string
  children: React.ReactNode
  position?: TooltipPosition
  maxWidth?: string
}

export const Tooltip: React.FC<TooltipProps> = ({
  tooltip,
  children,
  position = 'top',
  maxWidth = '300px',
}) => {
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({top: 0, left: 0, transform: ''})
  const [computedPosition, setComputedPosition] = useState<TooltipPosition>(position)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const offset = 8

  const calculateCoordsForPosition = (pos: TooltipPosition) => {
    if (!wrapperRef.current) return {top: 0, left: 0, transform: ''}
    const rect = wrapperRef.current.getBoundingClientRect()
    switch (pos) {
      case 'top':
        return {
          top: rect.top + window.scrollY - offset,
          left: rect.left + window.scrollX + rect.width / 2,
          transform: 'translateX(-50%) translateY(-100%)',
        }
      case 'bottom':
        return {
          top: rect.bottom + window.scrollY + offset,
          left: rect.left + window.scrollX + rect.width / 2,
          transform: 'translateX(-50%)',
        }
      case 'left':
        return {
          top: rect.top + window.scrollY + rect.height / 2,
          left: rect.left + window.scrollX - offset,
          transform: 'translateX(-100%) translateY(-50%)',
        }
      case 'right':
        return {
          top: rect.top + window.scrollY + rect.height / 2,
          left: rect.right + window.scrollX + offset,
          transform: 'translateY(-50%)',
        }
      default:
        return {
          top: rect.top + window.scrollY - offset,
          left: rect.left + window.scrollX + rect.width / 2,
          transform: 'translateX(-50%) translateY(-100%)',
        }
    }
  }

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    const tooltip = tooltipRef.current

    const handlePointerEnter = () => {
      const initialCoords = calculateCoordsForPosition(position)
      setCoords(initialCoords)
      setComputedPosition(position)
      setVisible(true)
    }

    const handlePointerLeave = (e: MouseEvent) => {
      const related = e.relatedTarget as Node | null
      if (wrapper?.contains(related) || tooltip?.contains(related)) {
        return
      }
      setVisible(false)
    }

    wrapper?.addEventListener('pointerenter', handlePointerEnter)
    wrapper?.addEventListener('pointerleave', handlePointerLeave)
    tooltip?.addEventListener('pointerleave', handlePointerLeave)

    return () => {
      wrapper?.removeEventListener('pointerenter', handlePointerEnter)
      wrapper?.removeEventListener('pointerleave', handlePointerLeave)
      tooltip?.removeEventListener('pointerleave', handlePointerLeave)
    }
  }, [position, visible])

  useLayoutEffect(() => {
    if (visible && tooltipRef.current && wrapperRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      // If tooltip overflows left or right, recalculate position
      if (tooltipRect.left < 0 || tooltipRect.right > window.innerWidth) {
        const wrapperRect = wrapperRef.current.getBoundingClientRect()
        const availableLeft = wrapperRect.left
        const availableRight = window.innerWidth - wrapperRect.right

        const fallback = availableRight >= availableLeft ? 'right' : 'left'
        if (fallback !== computedPosition) {
          const newCoords = calculateCoordsForPosition(fallback)
          setCoords(newCoords)
          setComputedPosition(fallback)
        }
      }
    }
  }, [visible, computedPosition])

  const getArrowPosition = () => {
    switch (computedPosition) {
      case 'bottom':
        return 'arrow-top'
      case 'left':
        return 'arrow-right'
      case 'right':
        return 'arrow-left'
      default:
        return 'arrow-bottom'
    }
  }

  return (
    <>
      <span ref={wrapperRef} className="wrapper">
        {children}
      </span>
      {visible &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            style={{
              top: coords.top,
              left: coords.left,
              transform: coords.transform,
              maxWidth: maxWidth,
            }}
            className={`tooltip ${getArrowPosition()}`}
          >
            <Text variant={TextVariant.caption} element="p" color={theme.palette.text.white}>
              {tooltip}
            </Text>
          </div>,
          document.body,
        )}

      <style jsx>{`
        .wrapper {
          position: relative;
        }

        .tooltip {
          position: absolute;
          background-color: ${theme.palette.tertiary.main};
          padding: ${theme.spacing[1]} ${theme.spacing[2]};
          border-radius: ${theme.spacing[1]};
          white-space: nowrap;
          z-index: 1000;
          max-width: ${maxWidth};
          box-sizing: border-box;
          word-break: break-word;
        }

        .tooltip::after {
          content: '';
          position: absolute;
          width: 0;
          height: 0;
          border: 6px solid transparent;
        }

        .arrow-top::after {
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-bottom-color: ${theme.palette.tertiary.main};
        }

        .arrow-bottom::after {
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-top-color: ${theme.palette.tertiary.main};
        }

        .arrow-left::after {
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          border-right-color: ${theme.palette.tertiary.main};
        }

        .arrow-right::after {
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          border-left-color: ${theme.palette.tertiary.main};
        }
      `}</style>
    </>
  )
}
